export const PROBLEMS = [
  {
    slug: "systems-dont-communicate",
    whyNow:
      "Disconnected systems don't get better on their own — every new hire, customer, and tool adds more manual glue. Fixing the connections now is cheaper than untangling them after the next growth spurt.",
    title: "Our systems don't communicate",
    angle: "Disconnected systems create manual work, data errors, and reporting delays.",
    impact:
      "When your tools don't share data, your team re-keys information between systems, mistakes creep in, and reporting becomes slow and unreliable. Over time, this manual glue quietly caps how much your business can grow.",
    signs: [
      "Staff re-enter the same data in multiple systems",
      "Numbers don't match across departments",
      "Reports take days to assemble by hand",
      "Integrations were built ad hoc and break often",
    ],
    related: ["systems-integration", "business-analysis", "data-reporting-bi"],
  },
  {
    slug: "outdated-software",
    whyNow:
      "Legacy systems fail on their schedule, not yours. Vendor support windows close, the people who understand the system move on, and an orderly migration becomes an emergency replacement.",
    title: "We rely on outdated software",
    angle: "Legacy systems can restrict growth, security, reporting, and vendor flexibility.",
    impact:
      "Aging software often works 'well enough' until it doesn't — limiting security, blocking better reporting, and locking you to a single vendor. The right move is to evaluate before a legacy system becomes a hard business constraint.",
    signs: [
      "Key systems are old and hard to support",
      "You can't get the reporting you need",
      "Security and compliance feel uncertain",
      "You're locked in to one aging vendor",
    ],
    related: ["software-modernization", "it-strategy-roadmapping", "project-leadership"],
  },
  {
    slug: "cybersecurity-worries",
    whyNow:
      "The cost gap between preventing an incident and recovering from one keeps widening — and insurers, clients, and regulators are asking harder questions every year.",
    title: "We're worried about cybersecurity",
    angle: "Cyber risk must be translated into business priorities and practical next steps.",
    impact:
      "Cybersecurity uncertainty keeps leaders up at night — but fear isn't a plan. The goal is a clear, honest picture of where you're actually exposed and a prioritized set of practical, business-sensible next steps.",
    signs: [
      "You're unsure of your real exposure",
      "Clients or regulators ask about security",
      "Security feels like only 'IT's problem'",
      "You're spending on tools without a plan",
    ],
    related: ["cybersecurity-assessment", "it-strategy-roadmapping", "cloud-infrastructure"],
  },
  {
    slug: "better-reporting",
    whyNow:
      "Every week leadership waits days for numbers is a week of decisions made late or on gut feel. Businesses with real-time visibility are simply moving faster.",
    title: "We need better reporting",
    angle: "Poor visibility leads to slow decisions and operational blind spots.",
    impact:
      "When reporting is manual and inconsistent, leadership makes decisions on gut feel and lagging information. Reliable, timely reporting turns scattered data into a clear, shared view of the business.",
    signs: [
      "Reports are manual and time-consuming",
      "Different teams report different numbers",
      "You lack visibility into key metrics",
      "Decisions wait on data that's hard to get",
    ],
    related: ["data-reporting-bi", "systems-integration", "business-analysis"],
  },
  {
    slug: "choosing-vendors",
    whyNow:
      "Software contracts signed under sales pressure become multi-year commitments. The evaluation you skip now is the lock-in you live with later.",
    title: "We need help choosing vendors",
    angle: "Vendor selection should be structured around business requirements, risk, cost, and fit.",
    impact:
      "Vendor decisions are high-stakes and high-pressure, and sales processes are built to persuade. A structured, objective evaluation helps you choose the right fit instead of the best pitch.",
    signs: [
      "You're comparing vendors under pressure",
      "Requirements aren't clearly defined",
      "Demos all start to look the same",
      "You're unsure about contract terms",
    ],
    related: ["vendor-selection", "business-analysis", "it-strategy-roadmapping"],
  },
  {
    slug: "technology-roadmap",
    whyNow:
      "Without a plan, every technology dollar is spent reactively — and reactive spending compounds. A roadmap turns next year's surprises into this year's line items.",
    title: "We need a technology roadmap",
    angle: "Move from reactive technology decisions to a clear, sequenced plan.",
    impact:
      "Without a roadmap, technology decisions are made one crisis at a time. A practical, budget-aligned roadmap gives leadership and IT a shared view of what's coming over the next one to three years.",
    signs: [
      "Decisions are reactive and crisis-driven",
      "Initiatives compete with no sequencing",
      "Budgeting brings repeated surprises",
      "There's no shared plan for the future",
    ],
    related: ["it-strategy-roadmapping", "business-analysis", "fractional-cto"],
  },
  {
    slug: "projects-stalling",
    whyNow:
      "Stalled projects don't just pause — they burn budget, morale, and stakeholder confidence daily. The sooner ownership and scope are reset, the more of the investment survives.",
    title: "Our projects keep stalling",
    angle: "Projects need scope clarity, governance, communication, and accountability.",
    impact:
      "Important initiatives stall when they compete with daily work and lack clear ownership. Structured project leadership brings the visibility and accountability needed to actually finish.",
    signs: [
      "Projects drift and deadlines slip",
      "Ownership and accountability are unclear",
      "Vendors and teams aren't coordinated",
      "Budgets and timelines overrun",
    ],
    related: ["project-leadership", "business-analysis", "it-strategy-roadmapping"],
  },
  {
    slug: "responsible-ai",
    whyNow:
      "AI adoption is happening in your business whether it's governed or not — employees may already be pasting company data into public tools. Guardrails now beat cleanup later.",
    title: "We need to use AI responsibly",
    angle: "Practical AI strategy that targets real value without unnecessary risk.",
    impact:
      "AI pressure is everywhere, but most pilots stall or create new risks. A responsible approach identifies genuine opportunities, weighs the risks honestly, and protects your data and reputation.",
    signs: [
      "You're unsure where AI adds value",
      "Data privacy and governance are concerns",
      "Pilots never reach production",
      "There's pressure to 'do something with AI'",
    ],
    related: ["ai-readiness", "it-strategy-roadmapping", "data-reporting-bi"],
  },
  {
    slug: "outgrowing-systems",
    whyNow:
      "Systems that strain at today's volume break at tomorrow's. Planning capacity ahead of growth is a project; discovering the limits during your busiest season is a crisis.",
    title: "We're growing faster than our systems can support",
    angle: "Scaling reveals the cracks in systems and processes that once worked.",
    impact:
      "Growth has a way of exposing every workaround. Systems that worked at a smaller scale start to slow you down — the fix is a deliberate plan to modernize and connect what matters most.",
    signs: [
      "Manual workarounds are everywhere",
      "Systems struggle under higher volume",
      "Onboarding and reporting can't keep up",
      "Growth is outpacing your technology",
    ],
    related: ["systems-integration", "software-modernization", "it-strategy-roadmapping"],
  },
  {
    slug: "executive-technology-leadership",
    whyNow:
      "Every major technology decision made without senior counsel is a bet — and the stakes rise with each contract, migration, and hire. Unwinding a wrong bet costs multiples of getting advice.",
    title: "We need executive technology leadership without a full-time CTO",
    angle: "Senior technology judgment at the leadership table, scaled to your needs.",
    impact:
      "Many growing businesses need executive-level technology judgment but can't justify a full-time CTO. A fractional technology advisor brings that guidance and discipline at a fraction of the cost.",
    signs: [
      "No senior technology voice at the table",
      "High-stakes decisions lack expert input",
      "Internal IT needs strategic direction",
      "A full-time CTO isn't yet justified",
    ],
    related: ["fractional-cto", "it-strategy-roadmapping", "cybersecurity-assessment"],
  },
];

export const getProblem = (slug) => PROBLEMS.find((p) => p.slug === slug);

// ---------- Methodology ----------
